import * as React from 'react';
import { styled } from '@linaria/react';

import { useTrackedOpacity } from './PartialKeyframes.helpers';
import OpacityLabel from './OpacityLabel';

interface Props {
  targetOpacity: number;
  variant: Variant;
  isToggled: boolean;
}

export type Variant = 'with-from' | 'without-from';

function FadeOutBall({ targetOpacity, variant, isToggled }: Props) {
  const buttonRef = React.useRef<HTMLDivElement>(null);
  let displayedOpacity = useTrackedOpacity(
    targetOpacity,
    buttonRef,
    // In the "fade in" examples, the ball has a "not rendered" state that we signal with this boolean. For this one, however, it’s always rendered, so we’ll lock it to `true`:
    true
  );

  // When toggling the fade-out animation, the opacity jumps to 1, but it only stays there for a brief fraction of a moment. It's hard to tell. So we’ll cheat and show the `1` for a tiny bit longer.

  if (
    displayedOpacity &&
    displayedOpacity >= 0.85 &&
    variant === 'with-from'
  ) {
    displayedOpacity = 1;
  }

  return (
    <Wrapper data-variant={isToggled ? variant : 'none'}>
      <OpacityLabel opacity={displayedOpacity} />
      <BallWrapper>
        <BallSocket />
        <BallElem
          ref={buttonRef}
          style={{ opacity: targetOpacity }}
        />
      </BallWrapper>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const BallWrapper = styled.div`
  position: relative;
`;

const BallSocket = styled.div`
  position: absolute;
  inset: 0;
  border: 2px solid var(--color-gray-300);
  border-radius: 45%;
  background: url('/images/partial-keyframes/transparent-white-16x16-10p.png')
    repeat;
`;

const BallElem = styled.div`
  --size: 64px;
  position: relative;
  background: hsl(50deg 100% 50%);
  border-radius: 45%;
  width: var(--size);
  height: var(--size);

  ${Wrapper}[data-variant='with-from'] & {
    animation: fadeOut 1000ms both linear;
  }

  ${Wrapper}[data-variant='without-from'] & {
    animation: fadeToTransparent 1000ms both linear;
  }
`;

export default FadeOutBall;
